import { FlexRow } from '@/components/common/Flex';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Box, BoxProps, FlexProps, MenuItem, Tooltip } from '@chakra-ui/react';
import { dt } from '@common/util/dateTime';
import { typedKeys } from '@common/util/objects';
import { toPx } from '@common/util/styles';
import { makeTickRange, TickRange, TickUnit } from '@common/valueSeries';
import { useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import ActionMenu from '../ActionMenu';
import { Button, IconButton, IconButtonProps, MenuButton } from '../Button';
import {
    ArrowNarrowLeft,
    ArrowNarrowRight,
    Calender,
    ChevronDown,
    Clock,
} from '../icons';
import { Text } from '../Typography';
import { chartTheme, DEFAULT_MARGIN } from './chartHelper';

export const CHART_RANGE_SELECTOR_HEIGHT = 32;
const CHART_RANGE_UNIT = 'month';

export type ChartRangeType = '1M' | '3M' | '1Y' | '5Y' | '10Y';

const chartRangeTypes: Record<
    ChartRangeType,
    { numMonths: number; rangeUnit?: TickUnit }
> = {
    '1M': { numMonths: 1, rangeUnit: 'day' },
    '3M': { numMonths: 3, rangeUnit: 'day' },
    '1Y': { numMonths: 12, rangeUnit: 'day' },
    '5Y': { numMonths: 12 * 5 },
    '10Y': { numMonths: 12 * 10 },
};

const chartRangeOptions = typedKeys(chartRangeTypes).map((value) => ({
    value,
}));

export interface ChartRangeControlsInput {
    key: string;
    minDate: string;
    maxDate: string;
    initialStartDate?: string;
    initialType?: ChartRangeType;
}

export const useChartRangeControls = ({
    key,
    initialStartDate,
    minDate,
    maxDate,
    initialType,
}: ChartRangeControlsInput): {
    range: TickRange;
    chartRangeType: ChartRangeType;
    onIncrement: VoidFunction;
    onDecrement: VoidFunction;
    onChangeType: (type: ChartRangeType) => void;
} => {
    const makeNewRange = useCallback(
        (startDate: string, type: ChartRangeType): TickRange => {
            const { numMonths, rangeUnit } = chartRangeTypes[type];

            // Clamp start
            let newStartDate = startDate < minDate ? minDate : startDate;
            let newEndDate = dt(newStartDate)
                .add(numMonths, CHART_RANGE_UNIT)
                .toDateStr();

            // Clamp end
            if (newEndDate > maxDate) {
                newEndDate = maxDate;
                newStartDate = dt(newEndDate)
                    .subtract(numMonths, CHART_RANGE_UNIT)
                    .toDateStr();
            }

            return makeTickRange(
                newStartDate,
                newEndDate,
                rangeUnit ?? CHART_RANGE_UNIT
            );
        },
        [minDate, maxDate]
    );

    const startType = initialType ?? '1M';
    const startDate = initialStartDate ?? initialStartDateForType(startType);

    const initialRange = makeNewRange(startDate, startType);
    const [range, setRange] = useLocalStorageState<TickRange>(
        `chart-range-${key}`,
        {
            defaultValue: initialRange,
        }
    );
    const [type, setType] = useLocalStorageState<ChartRangeType>(
        `chart-range-type-${key}`,
        {
            defaultValue: startType,
        }
    );

    const onIncrement = useCallback(() => {
        const { numMonths } = chartRangeTypes[type];
        const newStartDate = dt(range.startDate)
            .add(numMonths, CHART_RANGE_UNIT)
            .toDateStr();
        setRange(makeNewRange(newStartDate, type));
    }, [type, range, makeNewRange, setRange]);

    const onDecrement = useCallback(() => {
        const { numMonths } = chartRangeTypes[type];
        const newStartDate = dt(range.startDate)
            .subtract(numMonths, CHART_RANGE_UNIT)
            .toDateStr();
        setRange(makeNewRange(newStartDate, type));
    }, [type, range, makeNewRange, setRange]);

    const onChangeType = useCallback(
        (type: ChartRangeType) => {
            setType(type);
            setRange(makeNewRange(initialStartDateForType(type), type));
        },
        [makeNewRange, setRange, setType]
    );

    return {
        range,
        chartRangeType: type,
        onIncrement,
        onDecrement,
        onChangeType,
    };
};

const initialStartDateForType = (chartType: ChartRangeType): string => {
    const { numMonths } = chartRangeTypes[chartType];

    // Show 75% actual data, 25% historical data
    const percentageOfActualData = 0.75;

    switch (chartType) {
        case '1M': {
            // Need to handle 1M chartType separately because dayjs doesn't support subtracting a fraction of a month
            return dt()
                .subtract(30 * percentageOfActualData, 'days')
                .toDateStr();
        }
        default: {
            return dt()
                .subtract(
                    Math.round(numMonths * percentageOfActualData),
                    CHART_RANGE_UNIT
                )
                .toDateStr();
        }
    }
};

export interface ChartRangeSelectorProps extends Omit<BoxProps, 'onChange'> {
    type: ChartRangeType;
    onChange: (type: ChartRangeType) => void;
}

export function ChartRangeSelector({
    type,
    onChange,
    ...props
}: ChartRangeSelectorProps) {
    return (
        <Box
            position="absolute"
            right={2}
            top={toPx(-DEFAULT_MARGIN.top)}
            bg="white"
            {...props}
        >
            <ActionMenu
                menuButton={
                    <MenuButton
                        as={Button}
                        size="xs"
                        colorScheme="gray"
                        bg="white"
                        variant="ghost"
                        zIndex={chartTheme.zIndex.rangeMenuButton}
                        h={toPx(CHART_RANGE_SELECTOR_HEIGHT)}
                        onClickEvent="Chart Range Selector Menu Button Clicked"
                    >
                        <FlexRow align="center" color="textMuted" gap={1}>
                            <Clock size="sm" />
                            <Text fontSize="sm">{type}</Text>
                            <ChevronDown size="sm" />
                        </FlexRow>
                    </MenuButton>
                }
            >
                {chartRangeOptions.map(({ value }) => {
                    return (
                        <MenuItem
                            key={value}
                            onClick={() => {
                                onChange(value);
                            }}
                        >
                            <FlexRow alignItems="center" w="full" h="full">
                                {value}
                            </FlexRow>
                        </MenuItem>
                    );
                })}
            </ActionMenu>
        </Box>
    );
}

export interface ChartRangeStepperButtonProps extends FlexProps {
    onClick: VoidFunction;
    type: 'decrement' | 'increment';
    tooltip?: string;
    margin?: { top: number; bottom: number; left: number; right: number };
}

export const CHART_RANGE_STEPPER_BUTTON_WIDTH = 8;

export function ChartRangeStepperButton({
    onClick,
    type,
    tooltip,
    margin = DEFAULT_MARGIN,
    ...props
}: ChartRangeStepperButtonProps) {
    return (
        <FlexRow
            position="absolute"
            left={type === 'decrement' ? 0 : undefined}
            right={type === 'increment' ? 0 : undefined}
            top={toPx(margin.top)}
            bottom={toPx(margin.bottom)}
            w={0}
            zIndex={chartTheme.zIndex.rangeStepper}
            align="center"
            justify="center"
            {...props}
        >
            <ChartRangeStepperIconButton
                tooltip={tooltip}
                type={type}
                onClick={onClick}
                onClickEvent={
                    type === 'decrement'
                        ? 'Chart Range Decrement Button Clicked'
                        : 'Chart Range Increment Button Clicked'
                }
                size={CHART_RANGE_STEPPER_BUTTON_WIDTH}
                left={type === 'decrement' ? toPx(margin.left) : undefined}
                right={type === 'increment' ? toPx(margin.right) : undefined}
            />
        </FlexRow>
    );
}

interface ChartRangeStepperIconButtonProps
    extends Pick<ChartRangeStepperButtonProps, 'type' | 'tooltip'>,
        Omit<IconButtonProps, 'type' | 'size' | 'aria-label'> {
    size: IconButtonProps['w'];
}

export function ChartRangeStepperIconButton({
    onClick,
    type,
    tooltip,
    size,
    ...props
}: ChartRangeStepperIconButtonProps) {
    return (
        <TooltipWrapper tooltip={tooltip}>
            <IconButton
                bg="white"
                variant="ghost"
                colorScheme="gray"
                rounded="full"
                borderWidth={1}
                w={size}
                minW={size}
                maxW={size}
                h={size}
                minH={size}
                maxH={size}
                onClick={onClick}
                aria-label={type}
                color="text"
                {...props}
            >
                {type === 'decrement' ? (
                    <ArrowNarrowLeft size="sm" />
                ) : (
                    <ArrowNarrowRight size="sm" />
                )}
            </IconButton>
        </TooltipWrapper>
    );
}

const TooltipWrapper = ({
    children,
    tooltip,
}: {
    tooltip?: string;
    children: React.ReactNode;
}) => {
    if (!tooltip) {
        return <>{children}</>;
    }

    return (
        <Tooltip label={tooltip} aria-label={tooltip}>
            {children}
        </Tooltip>
    );
};

export function ChartDateRangeLabel({
    range,
    ...props
}: FlexProps & { range: TickRange }) {
    let startStr: string;
    let endStr: string;
    switch (range.unit) {
        case 'day':
        case 'week': {
            startStr = dt(range.startDate).format('MMM D, YYYY');
            endStr = dt(range.endDate).format('MMM D, YYYY');
            break;
        }
        default: {
            startStr = dt(range.startDate).format('MMM YYYY');
            endStr = dt(range.endDate).format('MMM YYYY');
        }
    }

    return (
        <FlexRow
            position="absolute"
            bg="transparent"
            top={4}
            left={18}
            align="center"
            color="gray.400"
            {...hoverStyleProps({ color: 'text', cursor: 'auto' })}
            {...props}
        >
            <Calender size="xs" mr={2} />
            <Text fontFamily="mono" fontSize="xs">
                {startStr} - {endStr}
            </Text>
        </FlexRow>
    );
}
